import {
	IconAdjustments,
	IconBrandLoom,
	IconBrandVimeo,
	IconBrandYoutube,
	IconFileText,
	IconHeadphones,
	IconPencil,
	IconQuestionMark,
	IconUpload,
} from "@tabler/icons-react"
import { type ComponentType, type FC } from "react"

import { type BokaderoIntendedPageType } from "@forento/core/models/bokadero"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import Button from "~/components/button"
import Modal from "~/components/modal"
import { query } from "~/utilities/trpc"

const pageOptions: OptionType[] = [
	{
		id: "text",
		label: "Text",
		description: "Skapa textbaserade sidor",
		Icon: IconFileText,
		gradient: tw`from-blue-500 to-cyan-500`,
		bgGradient: tw`hover:from-blue-200 hover:to-cyan-200`,
	},
	{
		id: "audio",
		label: "Ljud",
		description: "Skapa ljudbaserade sidor",
		Icon: IconHeadphones,
		gradient: tw`from-green-500 to-lime-500`,
		bgGradient: tw`hover:from-green-200 hover:to-lime-200`,
	},
	{
		id: "input",
		label: "Inmatning",
		description: "Formulär och interaktiva element",
		Icon: IconPencil,
		gradient: tw`from-emerald-500 to-teal-500`,
		bgGradient: tw`hover:from-emerald-200 hover:to-teal-200`,
	},
	{
		id: "quiz",
		label: "Quiz",
		description: "Svara på en fråga innan du kan fortsätta",
		Icon: IconQuestionMark,
		gradient: tw`from-purple-500 to-pink-500`,
		bgGradient: tw`hover:from-purple-200 hover:to-pink-200`,
	},
	{
		id: "custom",
		label: "Anpassad",
		description: "Avancerade anpassningar",
		Icon: IconAdjustments,
		gradient: tw`from-orange-500 to-red-500`,
		bgGradient: tw`hover:from-orange-200 hover:to-red-200`,
	},
]

const videoOptions: OptionType[] = [
	{
		id: "video",
		label: "Ladda upp",
		description: "Ladda upp din egna video",
		Icon: IconUpload,
		gradient: "from-indigo-500 to-purple-500",
		bgGradient: "hover:from-indigo-200 hover:to-purple-200",
	},
	{
		id: "youtube",
		label: "YouTube",
		description: "Bädda in YouTube-video",
		Icon: IconBrandYoutube,
		gradient: "from-red-500 to-pink-500",
		bgGradient: "hover:from-red-200 hover:to-pink-200",
	},
	{
		id: "vimeo",
		label: "Vimeo",
		description: "Bädda in Vimeo-video",
		Icon: IconBrandVimeo,
		gradient: "from-blue-500 to-indigo-500",
		bgGradient: "hover:from-blue-200 hover:to-indigo-200",
	},
	{
		id: "loom",
		label: "Loom",
		description: "Bädda in Loom-video",
		Icon: IconBrandLoom,
		gradient: "from-purple-500 to-violet-500",
		bgGradient: "hover:from-purple-200 hover:to-violet-200",
	},
]

type OptionType = {
	id: BokaderoIntendedPageType
	label: string
	description: string
	Icon: ComponentType<{ className?: string }>
	gradient: string
	bgGradient: string
}
type Props = { chapterId: string; isOpen: boolean; onClose(): void; reload(): Promise<unknown> }
const CreatePageModal: FC<Props> = ({ chapterId, isOpen, onClose, reload }) => {
	const createPage = query.createPage.useMutation({
		async onSuccess() {
			await reload()
			onClose()
		},
		onError(error) {
			console.error(error)
			alert("Ett fel uppstod. Försök igen.")
		},
	})

	function handleCreatePage(intendedType: BokaderoIntendedPageType) {
		createPage.mutate({ chapterId, intendedType })
	}

	return (
		<Modal isOpen={isOpen} className="flex flex-col gap-6">
			<div className="border-b border-slate-100 pb-6">
				<h1 className="text-2xl font-bold text-slate-900">Lägg till ny sida</h1>
				<p className="mt-1 text-sm text-slate-600">Välj typ av sida att skapa</p>
			</div>

			<OptionsSection
				title="Sidtyper"
				description="Välj grundläggande sidtyp för ditt innehåll"
				options={pageOptions}
				onSelect={handleCreatePage}
			/>
			<OptionsSection
				title="Video"
				description="Lägg till videoinnehåll från olika källor"
				options={videoOptions}
				onSelect={handleCreatePage}
			/>

			<div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
				<Button color="purple" onClick={onClose}>
					Avbryt
				</Button>
			</div>
		</Modal>
	)
}

type OptionsSectionProps = {
	title: string
	description: string
	options: OptionType[]
	onSelect(intendedType: BokaderoIntendedPageType): void
}
const OptionsSection: FC<OptionsSectionProps> = ({ title, description, options, onSelect }) => (
	<section>
		<h2 className="text-lg font-semibold text-slate-900">{title}</h2>
		<p className="mb-4 text-sm text-slate-600">{description}</p>
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
			{options.map(option => (
				<button
					key={option.id}
					type="button"
					onClick={() => onSelect(option.id)}
					className={cn(
						"flex cursor-pointer flex-col items-start rounded-xl border border-slate-200 bg-white p-6 text-left transition hover:-translate-y-1 hover:border-slate-300 hover:bg-gradient-to-br hover:shadow-lg hover:shadow-slate-200/50",
						option.bgGradient,
					)}
				>
					<div className={cn("mb-4 rounded-lg bg-gradient-to-r p-3 text-white shadow-lg", option.gradient)}>
						<option.Icon className="size-6" />
					</div>
					<h3 className="mb-2 font-semibold text-slate-900">{option.label}</h3>
					<p className="text-sm text-slate-600">{option.description}</p>
				</button>
			))}
		</div>
	</section>
)

export default CreatePageModal
